<template>
  
  <div class="meter-container">

    <!-- Values -->
    <div class="peak-container">
      <div class="peak_value"
           v-if="meter && meter.enabled"
      >
        <span v-if="peakMode === 'default'">{{ meter.peak }} </span>
        <span v-if="peakMode === 'fourSeconds'">{{ meter.peakFourSeconds }} </span>
        <span v-if="peakMode=== 'infinite'">{{ meter.peakInfinite }} </span>
      </div>
      <div class="rms_value">{{ meter.rms }}</div>
    </div>

    <!-- Meter -->
    <div class="track">
      <div 
        class   = "level" 
        :class  = "{disabled: !meter.enabled}"
      >
        <div class="peak"
             :style="'clip-path: inset(' + meter.peakPercentage + '% 0 0 0)'"
        ></div>
        <div class="rms"
             :style="'clip-path: inset(' + meter.rmsPercentage + '% 0 0 0)'"
        ></div>
        <div class="hold-peak"
             v-if="meter && meter.enabled && peakMode === 'fourSeconds' && audible && meter.peakFourSecondsPercentage !== 100"
             :style="{
               top: meter.peakFourSecondsPercentage + '%',
             }"
        >
        </div>
        <div class="hold-peak"
             v-if="meter && meter.enabled && peakMode === 'infinite' && audible && meter.peakInfinitePercentage !== 100"
             :style="{
               top: meter.peakInfinitePercentage + '%',
             }"
        >
        </div>
      </div>
    </div>

    <!-- Label -->
    <div
      class   = "label"
      :class  = "audible ? 'primary--text' : 'default--text'"
    >
      {{ label }}
    </div>

  </div>

</template>

<script lang="js">

// #region Imports

/* vuex */
import { mapState } from 'vuex';

// #endregion Imports

export default {

  // #region Properties

  name:  'AudioMeter',
  props: {
    meter: Object
  },
  computed: {
    ...mapState([
      'videoController',
      'video',
      'audio'
    ]),
    label() {

      const track = this.videoController?.currentAudioTrack;
      if (!track || !this.meter.enabled) {
        return '';
      }

      if (track.mappedChannelLabels && track.mappedChannelLabels[this.meter.channel]) {
        return track?.mappedChannelLabels[this.meter.channel];
      }

      if (track.channelLabels && track.channelLabels[this.meter.channel]) {
        return track.channelLabels[this.meter.channel];
      }

      return `${this.meter.channel + 1}`;
    },
    audible() {
      if (!this.video.canPlay) {
        return false;
      }

      const track = this.videoController?.currentAudioTrack;

      if (track?.currentSoundfield === 'Stereo') {
        return (track.left === this.meter.channel) || (track.right === this.meter.channel);
      }

      if (track?.currentSoundfield === 'Surround') {
        let channelLabel = this.label;

        return channelLabel === "L" ||
            channelLabel === "R" ||
            channelLabel === "C" ||
            channelLabel === "LFE" ||
            channelLabel === "Ls" ||
            channelLabel === "Rs";
      }

      return false;
    },
    peakMode(){
      return this.audio.peakMode;
    }
  }

  // #endregion Properties

}

</script>

<style lang="scss" scoped>

@import "../../../styles/colors.scss";

.meter-container {
  display:        flex;
  flex-direction: column;
  align-items:    center;
  width:          25px;
  max-width:      25px;

  .peak_value, .rms_value, .label {
    font-size:    12px;
    height:       20px;
  }

  .track {
    flex: 1;
    margin:       8px 0px;
  }
}

/* paint background of level meter */
.track {
  display:        flex;
  flex-direction: row;
  width:          100%;
}

/* paint not glowing LEDs */
.level {
  position: relative;
  margin: 0 2px;
  flex-grow: 1;
  z-index: 0;
  background-color: transparent;
  background-repeat: repeat-y;
  background-image: linear-gradient(to bottom, $darker-label-color 3px, transparent 1px);
  background-size: 100% 4px;

  &.disabled {
    background-image: linear-gradient(to bottom, $darkest-label-color 3px, transparent 1px);
  }
}

/* paint glowing LEDs */
.peak {
  width: 100%;
  height: 100%;
  content: ' ';
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 1;
  opacity: 0.35;
  background-repeat: no-repeat;
  background-image: linear-gradient(to top, green 0%, yellow 80%, red 100%);
  // light green:
  // background-image: linear-gradient(to top, rgb(0, 255, 0) 0%, rgb(0, 255, 0) 100%);
  background-size: 100% 100%;
  background-color: transparent;
}

/* paint glowing LEDs */
.rms {
  width: 100%;
  height: 100%;
  content: ' ';
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 2;
  background-repeat: no-repeat;
  background-image: linear-gradient(to top, green 0%, yellow 80%, red 100%);
  // light green:
  // background-image: linear-gradient(to top, rgb(0, 255, 0) 0%, rgb(0, 255, 0) 100%);
  background-size: 100% 100%;
}

.hold-peak {
  position: absolute;
  width: 100%;
  height: 3px;
  background-color: white;
  z-index: 4;
}

/* paint gap lines of LEDs */
.level::after {
  width: 100%;
  height: 100%;
  content: ' ';
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: 3;
  margin-top: 3px;
  background-image: linear-gradient(to bottom, #333 1px /* space between leds*/
  , transparent 1px);
  background-repeat: repeat-y;
  background-size: 100% 4px; /* 4px LED height */
  background-color: transparent;
}

.peak-container{
  height: 40px;
}

</style>  